import { useTranslation } from "react-i18next"

import type { FilthControlPlaneSummary } from "../filth-control-plane"
import type { PrismView } from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"

import { BrainCircuit, Server, ShieldCheck } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { PRISM_VIEW_OPTIONS } from "@/features/personalization/components/prism-view-constants"

type Props = {
  summary: FilthControlPlaneSummary
  visualizationEnabled: boolean
  visualizationPending: boolean
  visualizationAvailable?: boolean
  onVisualizationChange: (enabled: boolean) => void
  /** Company default Prism view; the select is hidden while it is unknown. */
  defaultView?: PrismView
  defaultViewPending?: boolean
  onDefaultViewChange?: (view: PrismView) => void
}

export function FilthControlPlaneOverview({
  summary,
  visualizationEnabled,
  visualizationPending,
  visualizationAvailable = true,
  onVisualizationChange,
  defaultView,
  defaultViewPending = false,
  onDefaultViewChange,
}: Props) {
  const { t } = useTranslation()
  const defaultViewLabel = t("adminDashboard.filthControlPlane.defaultView", {
    defaultValue: "Default view",
  })

  return (
    <Card className="border-border/70 bg-muted/20 shadow-none">
      <CardHeader className="gap-2 pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <BrainCircuit className="size-4" />
              {t("adminDashboard.filthControlPlane.title", {
                defaultValue: "Prism control plane",
              })}
            </CardTitle>
            <p className="text-muted-foreground mt-3 max-w-prose text-sm leading-relaxed">
              {t("adminDashboard.filthControlPlane.description", {
                defaultValue:
                  "Hello AI manages this connection and experience. Prism remains authoritative for sources, knowledge spaces, provenance, and access.",
              })}
            </p>
          </div>
          <Badge variant={summary.enabledBackends > 0 ? "secondary" : "outline"}>
            <Server className="mr-1 size-3" />
            {t("adminDashboard.filthControlPlane.active", {
              defaultValue: "{{enabled}} of {{total}} active",
              enabled: summary.enabledBackends,
              total: summary.totalBackends,
            })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="bg-background rounded-lg border p-4">
            <div className="text-lg font-semibold">{summary.primaryBackends}</div>
            <div className="text-muted-foreground text-xs">
              {t("adminDashboard.filthControlPlane.primary", {
                defaultValue: "{{count}} Context v1",
                count: summary.primaryBackends,
              })}
            </div>
          </div>
          <div className="bg-background rounded-lg border p-4">
            <div className="text-lg font-semibold">{summary.shadowBackends}</div>
            <div className="text-muted-foreground text-xs">
              {t("adminDashboard.filthControlPlane.shadow", {
                defaultValue: "{{count}} shadow",
                count: summary.shadowBackends,
              })}
            </div>
          </div>
          <div className="bg-background rounded-lg border p-4">
            <div className="text-lg font-semibold">{summary.legacyBackends}</div>
            <div className="text-muted-foreground text-xs">
              {t("adminDashboard.filthControlPlane.legacy", {
                defaultValue: "{{count}} legacy/off",
                count: summary.legacyBackends,
              })}
            </div>
          </div>
        </div>

        <div className="bg-background flex items-center justify-between gap-4 rounded-lg border p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="size-4" />
              {t("adminDashboard.filthControlPlane.visualization", {
                defaultValue: "Secure neural retrieval",
              })}
            </div>
            <p className="text-muted-foreground mt-2 max-w-prose text-sm leading-relaxed">
              {t("adminDashboard.filthControlPlane.visualizationHint", {
                defaultValue:
                  "Show privacy-safe Prism retrieval activity in Hello AI. Disabled by default.",
              })}
            </p>
          </div>
          <Switch
            checked={visualizationEnabled}
            disabled={!visualizationAvailable || visualizationPending}
            aria-label={t("adminDashboard.filthControlPlane.visualization", {
              defaultValue: "Secure neural retrieval",
            })}
            onCheckedChange={onVisualizationChange}
          />
        </div>

        {defaultView !== undefined && onDefaultViewChange ? (
          <div className="bg-background flex flex-wrap items-center justify-between gap-6 rounded-lg border p-4">
            <div className="min-w-0">
              <Label className="text-sm font-medium" htmlFor="filth-prism-default-view">
                {defaultViewLabel}
              </Label>
              <p className="text-muted-foreground mt-2 max-w-prose text-sm leading-relaxed">
                {t("adminDashboard.filthControlPlane.defaultViewHint", {
                  defaultValue:
                    "The Prism view users see unless they choose their own in Preferences.",
                })}
              </p>
            </div>
            <Select
              disabled={!visualizationAvailable || defaultViewPending}
              value={defaultView}
              onValueChange={(value) => onDefaultViewChange(value as PrismView)}
            >
              <SelectTrigger
                aria-label={defaultViewLabel}
                className="min-h-10 w-full sm:w-64"
                id="filth-prism-default-view"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRISM_VIEW_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey, { defaultValue: option.fallback })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
